<template>
    <bill-shell>
        <!-- Hero / Intro -->
        <section id="smartsuite" class="section py-5 bg-white">
            <div class="container text-left">
                <!-- H1 -->
                <h1 class="text-8 font-weight-bold mb-4 mt-2">
                    From SmartBiz to SmartSuite Bundles: Explore Econet’s New Internet Solution
                </h1>

                <!-- Intro paragraphs -->
                <p class="text-3">
                    In an era where connectivity is king, Econet Zimbabwe has introduced an upgraded offering to replace its previous SmartBiz product: SmartSuite bundles. If you've been using SmartBiz or looking for more flexible internet data plans, this transition marks a pivotal shift in how Econet delivers fixed wireless and data packages.
                </p>
                <p class="text-3">
                    And the best part? With Magetsi, you don’t have to leave your home or office to buy the SmartSuite bundles. We already help thousands of  Zimbabweans pay bills, buy ZESA tokens, airtime, TelOne Wi-Fi — and now, it’s your one-stop shop to top up with Econet SmartSuite Data Packages quickly and securely online.
                </p>

                <!-- First packages table (Package / Data / Price) -->
                <div class="mt-4">
                    <table class="table table-bordered text-3">
                        <thead>
                        <tr>
                            <th>Package</th>
                            <th>Data Allowance</th>
                            <th>Price (USD)</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr><td>SmartLite</td><td>50GB</td><td>$30</td></tr>
                        <tr><td>SmartPlus</td><td>75GB</td><td>$40</td></tr>
                        <tr><td>SmartMax</td><td>100GB</td><td>$50</td></tr>
                        <tr><td>SmartFlex</td><td>200GB</td><td>$70</td></tr>
                        <tr><td>SmartUltra</td><td>400GB</td><td>$99</td></tr>
                        <tr><td>SmartPro</td><td>800GB</td><td>$170</td></tr>
                        </tbody>
                    </table>
                </div>

                <!-- CTA Button -->
                <div class="text-center mt-4">
                    <router-link to="/airtime/smartsuite" class="btn btn-sm btn-primary btn-lg">
                        Buy SmartSuite Bundles
                    </router-link>
                </div>

                <!-- In this article list -->
                <div class="mt-5">
                    <p class="text-3 mb-2">In this article, we’ll walk you through:</p>
                    <ul class="text-3">
                        <li>What Econet SmartSuite bundles are.</li>
                        <li>How to access and subscribe to them easily.</li>
                        <li>The transition from SmartBiz to SmartSuite.</li>
                        <li>Features that make SmartSuite bundles stand out.</li>
                        <li>Why Magetsi is the easiest way to buy them.</li>
                        <li>A detailed FAQ answering everything you need to know.</li>
                    </ul>
                </div>
            </div>
        </section>

        <!-- What are SmartSuite (H2) -->
        <section class="section py-5 bg-light-4" id="what-is-smartsuite">
            <div class="container">
                <h2 class="text-7 font-weight-bold mb-4 text-left">What are Econet SmartSuite Bundles?</h2>
                <p class="text-3">
                    The SmartSuite bundle (sometimes stylized “SmartSuite” or “Smart-Suite”) is Econet’s new suite of Fixed Wireless Access (FWA) and broadband-oriented data plans designed for home, office, or business users. The SmartSuite bundles replace the earlier SmartBiz plans, with a broader set of tiers aimed at different usage needs and budgets.
                </p>
                <p class="text-3">
                    These SmartSuite bundles are structured to provide clear data allowances and pricing, moving away from the “unlimited but with fair usage caps” model of SmartBiz. With SmartSuite, users can choose the plan that best suits their internet habits: light, moderate, or heavy usage.
                </p>
                <p class="text-3">
                    On Econet’s official site, the SmartSuite packages are listed with names such as SmartLite, SmartPlus, SmartMax, SmartFlex, and possibly higher tiers. These bundles tend to offer a more predictable cost and usage structure, which can help households and businesses better manage their monthly internet spending.
                </p>
            </div>
        </section>

        <!-- How to Access (H2) -->
        <section class="section py-5 bg-white" id="how-to-access">
            <div class="container">
                <h2 class="text-7 font-weight-bold mb-4 text-left">How to Access Econet SmartSuite Bundles.</h2>
                <p class="text-3">
                    When Econet launched SmartSuite, many customers wondered if the process would be complicated. We’ve worked hard at Magetsi to simplify it. Here’s a step-by-step guide:
                </p>
                <ol class="text-3">
                    <li><strong>Step 1: Confirm coverage in your area</strong> — SmartSuite uses Econet’s Fixed Wireless Access (FWA) network, which is powered by LTE. Coverage is available in most urban and peri-urban areas. If you already had SmartBiz, your location likely qualifies for SmartSuite.</li>
                    <li><strong>Step 2: Get your SmartSuite line and router</strong> — You’ll need a SmartSuite SIM and router device compatible with Econet’s FWA network. You can access this at your nearest Econet Shop.</li>
                    <li><strong>Step 3: Activate your service</strong> — Once you have the device, Econet will activate the SmartSuite line. After activation, you’ll be able to start buying SmartSuite bundles.</li>
                    <li><strong>Step 4: Top-up Your SmartSuite bundle on Magetsi</strong> — This is where we make life easy. You don’t have to go back to a shop every time you need a top-up. Just visit Magetsi  or Magetsi WhatsApp Chatbot, select SmartSuite bundles, and choose the package that fits your usage.</li>
                    <li><strong>Step 5: Pay securely</strong> — You can pay from your EcoCash wallet, Mastercard/ VisaCard.</li>
                    <li><strong>Step 6: Enjoy seamless internet</strong> — Once your payment is confirmed, your bundle is active. You’re free to stream, browse, work, or study with full control of your data.</li>
                </ol>
                <p class="text-3">
                    For us at Magetsi, the goal is simple: to make it 24/7 easy to buy your SmartSuite bundles.
                </p>

                <!-- Transitioning (H3) -->
                <h3 class="text-6 font-weight-bold mt-5">Transitioning from Smarbiz to SmartSuite Bundles</h3>
                <p class="text-3">
                    We know this change has raised questions. Many of our customers at Magetsi used SmartBiz since the time it was rolled out, so the transition to SmartSuite might feel uncertain. Here’s what you need to know:
                </p>
                <ul class="text-3">
                    <li>SmartBiz is being phased out – Econet is moving all users to SmartSuite.</li>
                    <li>SmartSuite is more transparent – You know the data size upfront.</li>
                    <li>Costs may feel different – SmartBiz had “fair usage caps” that throttled heavy users. SmartSuite instead gives set GBs.</li>
                    <li>Migration is smooth – if you had SmartBiz, you can migrate to SmartSuite without losing access. At Magetsi, we’ve supported many of our customers through this shift, ensuring their payments and top-ups continue without interruption.</li>
                    <li>Routers are required – SmartSuite runs on Econet’s Fixed Wireless Access (FWA) network. If you didn’t already have a SmartBiz router, you’ll need to get one from your nearest Econet Shop before you can start using SmartSuite.</li>
                </ul>
                <p class="text-3">
                    For us, the key is to help you adjust your data habits. If you used to rely on SmartBiz’s “unlimited” option, you may now want to track your actual monthly usage and pick the SmartSuite tier that best matches.
                </p>
                <p class="text-3">
                    And the good news? With Magetsi, you don’t have to worry about missing renewal dates or standing in long queues. You can top up online instantly.
                </p>
            </div>
        </section>

        <!-- Packages (H2) with Best For -->
        <section class="section py-5 bg-light-4" id="packages">
            <div class="container">
                <h2 class="text-7 font-weight-bold mb-4 text-left">Econet SmartSuite Data Packages</h2>

                <table class="table table-bordered text-3">
                    <thead>
                    <tr>
                        <th>Package</th>
                        <th>Data Allowance</th>
                        <th>Price (USD)</th>
                        <th>Best For</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr><td>SmartLite</td><td>50GB</td><td>$30</td><td>Light users</td></tr>
                    <tr><td>SmartPlus</td><td>75GB</td><td>$40</td><td>Families</td></tr>
                    <tr><td>SmartMax</td><td>100GB</td><td>$50</td><td>Bigger households, freelancers</td></tr>
                    <tr><td>SmartFlex</td><td>200GB</td><td>$70</td><td>Flexible scaling, small offices</td></tr>
                    <tr><td>SmartUltra</td><td>400GB</td><td>$99</td><td>Heavy, multi-users, SMEs</td></tr>
                    <tr><td>SmartPro</td><td>800GB</td><td>$170</td><td>Established SMEs</td></tr>
                    </tbody>
                </table>
            </div>
        </section>

        <!-- Features (H3) -->
        <section class="section py-5 bg-white" id="features">
            <div class="container">
                <h3 class="text-6 font-weight-bold">Features of SmartSuite Bundles</h3>
                <p class="text-3">SmartSuite bundles bring a number of advantages that make them perfect for modern internet users in Zimbabwe.</p>
                <ol class="text-3">
                    <li><strong>Transparent Data Allocations</strong><br/>Unlike SmartBiz’s fair usage policy, SmartSuite tells you exactly how much data you have — whether it’s 50GB, 75GB, or 200GB. This clarity helps you plan and budget.</li>
                    <li><strong>Speed and Stability</strong><br/>SmartSuite is powered by Econet’s LTE/FWA network. In practice, this means faster streaming, smoother Zoom calls, and more reliable gaming compared to mobile bundles.</li>
                    <li><strong>Flexible Options for Different Users</strong><br/>
                        Light users: SmartLite is perfect if you just browse, email, or do light streaming.<br/>
                        Moderate users: SmartPlus or SmartMax work for families who watch Netflix, YouTube, and use social media daily.<br/>
                        Heavy users: SmartFlex (200GB) suits offices or households with multiple heavy users.
                    </li>
                    <li><strong>Seamless Payments on Magetsi</strong><br/>At Magetsi, we’ve built SmartSuite into the same platform you already trust for ZESA tokens, airtime, and TelOne Wi-Fi. This means you manage all your digital needs in one place.</li>
                    <li><strong>Diaspora Advantage</strong><br/>If you’re in the UK, South Africa, or anywhere abroad, you can use Magetsi to pay for SmartSuite bundles for family back home. It’s instant and reliable.</li>
                    <li><strong>Scalability for Work and Study</strong><br/>With remote work and online learning growing in Zimbabwe, SmartSuite offers scalable internet that can handle Zoom, Teams, or Google Meet.</li>
                </ol>
            </div>
        </section>

        <!-- Why Buy (H2) -->
        <section class="section py-5 bg-light-4" id="why-magentsi">
            <div class="container">
                <h2 class="text-7 font-weight-bold mb-4 text-left">Why Buy SmartSuite Bundles on Magetsi?</h2>
                <p class="text-3">We get this question a lot. After all, Econet offers its own ways to pay. But here’s why Magetsi is the smarter choice:</p>
                <ul class="text-3">
                    <li>Convenience: No queues, no store visits — top up anytime, anywhere.</li>
                    <li>Multiple Payment methods: Pay from your EcoCash wallet or use you’re your Master/Visa Card.</li>
                    <li>Diaspora-friendly: Support your family in Zimbabwe directly from abroad.</li>
                    <li>All-in-one platform: Manage ZESA tokens, airtime, TelOne Wi-Fi, and SmartSuite bundles together.</li>
                    <li>Instant confirmation: Your bundle is active immediately after payment.</li>
                    <li>Trusted brand: Thousands already use Magetsi daily for essential services.</li>
                </ul>
                <p class="text-3">For us, it’s not just about selling bundles. It’s about making life easier for Zimbabweans everywhere.</p>
            </div>
        </section>

        <!-- FAQs (H2) -->
        <section class="section py-5 bg-white" id="faqs">
            <div class="container">
                <h2 class="text-7 font-weight-bold mb-4 text-left">FAQs on Econet SmartSuite Bundles</h2>

                <!-- Q1 -->
                <h4 class="text-5 font-weight-bold mb-2">Q1: What exactly is a SmartSuite bundle?</h4>
                <p class="text-3">It’s Econet’s new internet package that replaces SmartBiz. You buy a set amount of data (e.g., 100GB) for a fixed price.</p>

                <!-- Q2 -->
                <h4 class="text-5 font-weight-bold mb-2">Q2: Can I buy SmartSuite bundles on Magetsi?</h4>
                <p class="text-3">Yes! As long as you have your SmartSuite line, you can buy or renew bundles on Magetsi instantly.</p>

                <!-- Q3 -->
                <h4 class="text-5 font-weight-bold mb-2">Q3: How do I migrate from SmartBiz to SmartSuite?</h4>
                <p class="text-3">There is no need to take any action to switch to SmartSuite .  Econet automatically switched all SmartBiz users to SmartSuite.</p>

                <!-- Q4 -->
                <h4 class="text-5 font-weight-bold mb-2">Q4: What happens if I finish my bundle early?</h4>
                <p class="text-3">You can buy another bundle right away on Magetsi. No waiting for the next billing cycle.</p>

                <!-- Q5 -->
                <h4 class="text-5 font-weight-bold mb-2">Q5: Can diaspora users pay for SmartSuite bundles?</h4>
                <p class="text-3">Absolutely. Many of our Magetsi customers abroad top up bundles for their families in Zimbabwe.</p>

                <!-- Q6 -->
                <h4 class="text-5 font-weight-bold mb-2">Q6: Are SmartSuite bundles faster than SmartBiz?</h4>
                <p class="text-3">Yes. They are powered by LTE/FWA and tend to offer more stable speeds without the hidden throttling of SmartBiz.</p>

                <!-- Q7 -->
                <h4 class="text-5 font-weight-bold mb-2">Q7: Can I buy SmartSuite alongside my ZESA tokens?</h4>
                <p class="text-3">Yes. That’s one of Magetsi’s strengths — you can pay for multiple bills on .one platform.</p>

                <!-- Q8 -->
                <h4 class="text-5 font-weight-bold mb-2">Q8: Do SmartSuite bundles roll over if I don’t use them all?</h4>
                <p class="text-3">Most expire at the end of the month. It’s best to choose the package that matches your needs.</p>

                <!-- Q9 -->
                <h4 class="text-5 font-weight-bold mb-2">Q9: Can businesses use SmartSuite?</h4>
                <p class="text-3">Yes. Many offices and SMEs are already switching to SmartSuite, and they use Magetsi for easy monthly payments.</p>

                <!-- Q10 -->
                <h4 class="text-5 font-weight-bold mb-2">Q10: What payment methods does Magetsi support?</h4>
                <p class="text-3">You can pay from your EcoCash wallet or use a Visa/MasterCard</p>
            </div>
        </section>

        <!-- Closing -->
        <section class="section py-5 bg-light-4" id="closing">
            <div class="container">
                <h3 class="text-6 font-weight-bold">SmartSuite + Magetsi = A Smarter Way to Connect</h3>
                <p class="text-3">
                    At Magetsi, we’ve seen firsthand how connectivity changes lives. Whether it’s a student learning online, a professional working remotely, or a family streaming together at night, reliable internet has become essential.
                </p>
                <p class="text-3">
                    With Econet SmartSuite Bundles, Zimbabweans have added another internet option with are clear, flexible, and designed for today’s needs. And with Magetsi, you get the easiest way to buy, renew, and manage those bundles, alongside all your other essentials like ZESA tokens, airtime, and TelOne.
                </p>
                <p class="text-3">
                    So if you’re ready to move on from SmartBiz, don’t stress. SmartSuite is here, and Magetsi is ready to keep you connected.
                </p>

                <!-- Repeat CTA -->
                <div class="text-center mt-4">
                    <router-link to="/airtime/smartsuite" class="btn btn-sm btn-primary btn-lg">
                        Buy SmartSuite Bundles
                    </router-link>
                </div>
            </div>
        </section>
    </bill-shell>
</template>

<script>
import BillShell from "../bill-shell.vue";
import { appDataMixin } from "../../../core/data/global";

export default {
    name: "smartsuite-bundles",
    components: { BillShell },
    mixins: [appDataMixin],
    metaInfo() {
        const url = "https://magetsi.co.zw/smartsuite-bundles";
        return {
            title: "Econet SmartSuite Bundles – Magetsi",
            meta: [
                {
                    hid: "description",
                    name: "description",
                    content:
                        "Get access to Econet's new SmartSuite bundles, replacing Smartbiz. Choose from different packages that suit your needs and pay on Magetsi for instant top-ups."
                },
                { property: "og:title", content: "Econet SmartSuite Bundles – Magetsi" },
                {
                    property: "og:description",
                    content:
                        "Get access to Econet's new SmartSuite bundles, replacing Smartbiz. Choose the right package and pay on Magetsi for instant top-ups."
                },
                { property: "og:url", content: url },
                { property: "og:type", content: "article" },
                { property: "og:site_name", content: "Magetsi" }
            ],
            link: [{ rel: "canonical", href: url }]
        };
    },
    data: () => ({
        providers: ["Econet", "NetOne", "Telecel"],
        unavailable: ["Telecel"]
    }),
    mounted() {
        this.switchCurrency({ name: "" });
    },
    computed: {
        provider() {
            return this.$route.query.provider || this.providers[0];
        }
    }
};
</script>
